import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

const SESSION_PASSED_KEY = 'parentalGatePassed';

/** Why the gate is shown; selects the explanation displayed to the grown-up. */
export type ParentalGateReason = 'externalLink' | 'parentsArea';

export interface ParentalGateRequest {
  reason: ParentalGateReason;
  resolve: (allowed: boolean) => void;
}

/**
 * Parental gate required by Apple (App Store Review Guideline 1.3 / 5.1.4) before
 * any link leaves the app or any purchase is started. Usable from anywhere:
 *  - code:     `await parentalGate.request()` or `parentalGate.guard(() => ...)`
 *  - template: `<a (wktParentalGate)="openSomething()">` (ParentalGateDirective)
 * The single <wkt-parental-gate> in AppComponent renders the challenge.
 */
@Injectable({
  providedIn: 'root',
})
export class ParentalGateService {
  readonly requests$ = new Subject<ParentalGateRequest>();

  request(reason: ParentalGateReason = 'externalLink'): Promise<boolean> {
    return new Promise((resolve) => this.requests$.next({ reason, resolve }));
  }

  /**
   * Like `request()`, but once passed it stays open for the rest of the webview
   * session. Used for the parents-only area (sign-in, subscriptions) so a
   * parent isn't challenged on every screen. External links still use `request()`.
   */
  async requestForSession(): Promise<boolean> {
    if (this.readSessionFlag()) {
      return true;
    }
    const allowed = await this.request('parentsArea');
    if (allowed) {
      try {
        sessionStorage.setItem(SESSION_PASSED_KEY, 'true');
      } catch {
        // Storage unavailable: the parent will simply be asked again next time.
      }
    }
    return allowed;
  }

  /** Runs `action` only if the gate is (or was already, this session) passed. For purchases/subscriptions. */
  guardForSession(action: () => void): void {
    this.requestForSession().then((allowed) => {
      if (allowed) {
        action();
      }
    });
  }

  /** Runs `action` only if the grown-up passes the gate. */
  guard(action: () => void): void {
    this.request().then((allowed) => {
      if (allowed) {
        action();
      }
    });
  }

  private readSessionFlag(): boolean {
    try {
      return sessionStorage.getItem(SESSION_PASSED_KEY) === 'true';
    } catch {
      return false;
    }
  }
}
